/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-10-07T08:44:05.541Z
 * 소스 파일: src/data/dto/treatment-source/add-treatment-response-dto
 */

import type { AddTreatmentResponseDto } from "@/data/dto/treatment-source/add-treatment-response-dto";

/**
 * AddTreatmentResponseDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isAddTreatmentResponseDto(input: unknown): input is AddTreatmentResponseDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    typeof obj.newTreatmentUuid === "string" &&
    (obj.terminatedTreatmentUuid == null || typeof obj.terminatedTreatmentUuid === "string")
  );
}

/**
 * AddTreatmentResponseDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateAddTreatmentResponseDto(input: unknown): IValidation<AddTreatmentResponseDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // newTreatmentUuid (required)
      if (objObj.newTreatmentUuid === undefined) {
        errors.push({ path: "newTreatmentUuid", expected: "string", value: undefined });
      } else {
        if (typeof objObj.newTreatmentUuid !== "string") {
          errors.push({ path: "newTreatmentUuid", expected: "string", value: objObj.newTreatmentUuid });
        }
      }
      // terminatedTreatmentUuid (required)
      if (objObj.terminatedTreatmentUuid === undefined) {
        errors.push({ path: "terminatedTreatmentUuid", expected: "string", value: undefined });
      } else {
        if (!(objObj.terminatedTreatmentUuid == null || typeof objObj.terminatedTreatmentUuid === "string")) {
          errors.push({ path: "terminatedTreatmentUuid", expected: "string | null", value: objObj.terminatedTreatmentUuid });
        }
      }
    }
  
  if (errors.length === 0 && isAddTreatmentResponseDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "AddTreatmentResponseDto",
        value: input,
      },
    ],
  };
}

/**
 * AddTreatmentResponseDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertAddTreatmentResponseDto(input: unknown): AddTreatmentResponseDto {
  const validation = validateAddTreatmentResponseDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertAddTreatmentResponseDto] :: 검증 실패: AddTreatmentResponseDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
